import {
  BASE_PLAN_SUB_TYPES,
  BILLING_CYCLE,
  BillingCycleEnum,
  BUNDLE_PLAN_CODES_ONLINE_SUPPORTED,
  maxBizAndBizPlusLicenseCount,
  limitLicenseWithBaseZoneTypes,
  PBX_CATEGORYS,
  planTypes,
  ZONE_BIZ_PLUS_PARENT_CATEGORY,
  ZONE_PRO_PLUS_PARENT_CATEGORY,
  ZOOM_EVENTS_SUB_TYPES,
  ZOOM_MEETING_PRO_MIN_LICENSE,
  ZOOM_MEETING_EDU_PLUS_MIN_LICENSE,
  unlockBizAndBizPlusCountByRetention
} from '@/common/constants';
import { plansof } from '@/utils/chain';
import { translation } from '@/plugins/i18n';
import store from '@/store';
import Vue from 'vue';
import { getMeetingPlanByCategory, isBizPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import {
  computedPbxBasePlans,
  computedPbxNonBasePlans,
  computedPbxPlans,
  PBX_SUB_TYPES,
} from '@/common/service/computers/plans/pbx';
import { $context, $query } from '@/global-install';
import _, { get, lowerCase, uniqBy } from 'lodash';
import { dateTimeFormat } from '@/utils/dateformat';
import { pageConfig } from '@/common/service/page-config';
import { getBasePlanInfo } from '@/common/product';
import { parseConsumptionCode } from '@/plans/EVENT_SAAS/helper/parse';
import { flowTypes } from '@zoom/billing-payment';
import { isProductExcludedInShoppingCart } from '@/views/config/shopping-cart/persist/plan-remove';

export const totalCostMap = (planMapFlat, getSelectedPlans) => {
  const licenseMap = {}
  getSelectedPlans.forEach(p => {
    licenseMap[p.category.toUpperCase()] = p.cartInfo.selected_number;
  });
  const costOf = (cycle) => {
    const plans = plansof(planMapFlat).type(planTypes.ZOOM_ONE_BUNDLE).cycle(cycle);
    return plans.plans.reduce((pre, p) => {
      if (!licenseMap[p.category.toUpperCase()]) return pre;
      return (licenseMap[p.category.toUpperCase()] * p.amount) + pre;
    }, 0);
  };
  return {
    [BILLING_CYCLE.MONTHLY]: costOf(BILLING_CYCLE.MONTHLY),
    [BILLING_CYCLE.ANNUAL]: costOf(BILLING_CYCLE.ANNUAL)
  };
}

export const costMap = (planMapFlat, callingPlanRegions) => {
  const costOf = (cycle) => {
    const plans = plansof(planMapFlat).type(planTypes.ZOOM_ONE_BUNDLE).cycle(cycle);
    return plans.clone().region(callingPlanRegions?.[0]?.id).get(0)?.amount || 0;
  };
  return {
    [BILLING_CYCLE.MONTHLY]: costOf(BILLING_CYCLE.MONTHLY),
    [BILLING_CYCLE.ANNUAL]: costOf(BILLING_CYCLE.ANNUAL),
    [BILLING_CYCLE.QUARTERLY]: costOf(BILLING_CYCLE.QUARTERLY),
    [BILLING_CYCLE.TWO_YEARS]: costOf(BILLING_CYCLE.TWO_YEARS),
    [BILLING_CYCLE.THREE_YEARS]: costOf(BILLING_CYCLE.THREE_YEARS),
  };
};

export const checkBizPlusLicense = () => {
  if (!isBizPlusContext || $context.is.edit || isProductExcludedInShoppingCart(store.getters.getSelectedPlans)) return { message: '' };
  const { totalZOLicenses: total, currentSelectedMeetingLicenses, boughtBizPlusPlan, bundleLicenses } = store.getters.plansofBizBundle;
  // if add-to-cart & purchased one region plan
  if ($context.is.add2cart && boughtBizPlusPlan.length > 0) return checkBizPlusAddonLicense();
  const isMixMatch = currentSelectedMeetingLicenses > 0;
  if ($context.is.add2cart && bundleLicenses > maxBizAndBizPlusLicenseCount) {
    return { message: translation('billing.common.max_license_exceed_contact_us', [maxBizAndBizPlusLicenseCount, maxBizAndBizPlusLicenseCount+1]) };
  }

  if (total > maxBizAndBizPlusLicenseCount && ($context.is.buyNow || $context.is.basic2pro)) {
    return { message: translation('billing.common.max_license_exceed_contact_us', [maxBizAndBizPlusLicenseCount, maxBizAndBizPlusLicenseCount+1]) };
  }
  if (!isMixMatch) {
    if (total < ZOOM_MEETING_PRO_MIN_LICENSE) {
      return { message: translation('billing.zobizplus.atlease_desc') };
    }
  } else {
    return { message: '' }; // HACK mix & match always auto added to meet the requirement;
  }
  return { message: '' };
};

export const plansofBizBundle = (getters = store.getters) => {
  const { getSelectedPlans, boughtPlans, getUserCurrentSubscriptions, subscriptions, assignInfo } = getters;
  const selectedBizPlan = plansof(getSelectedPlans).type(planTypes.ZOOM_MEETING).not().category(BASE_PLAN_SUB_TYPES.PRO).get();
  const boughtBizPlan = plansof(boughtPlans).type(planTypes.ZOOM_MEETING).not().category(BASE_PLAN_SUB_TYPES.PRO).get();
  const selectedBizPlusPlan = plansof(getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
  const boughtBizPlusPlan = plansof(boughtPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
  const plans = [...selectedBizPlan, ...selectedBizPlusPlan, ...boughtBizPlan, ...boughtBizPlusPlan];
  const selectedPlans = uniqBy(plans, 'plan_code');
  const selectedAndBoughtBizPlusPlans = uniqBy([...selectedBizPlusPlan, ...boughtBizPlusPlan], 'plan_code');
  const plansofSelectedPlans = plansof(selectedPlans);
  // just for bizPlus !!!
  const bundleLicenses = plansofSelectedPlans.clone().type(planTypes.ZOOM_ONE_BUNDLE).sum('license');
  const meetingLicenses = plansofSelectedPlans.clone().type(planTypes.ZOOM_MEETING).not().category(BASE_PLAN_SUB_TYPES.PRO).sum('license');
  const currentSelectedMeetingLicenses = selectedBizPlan.reduce((p, v) => p + v?.cartInfo?.selected_number ?? 0, 0);
  const pbxBasePlan = get(computedPbxPlans(boughtPlans), '0', {});
  const selectedPbxBasePlan = get(computedPbxPlans(getSelectedPlans), '0', {});
  const maxLicensesAddonPlan = getMaxLicensesAddonPlan(getUserCurrentSubscriptions);
  const hasFreeTrial = subscriptions.find(p => p.isFreeTrial && p.type === planTypes.ZOOM_ONE_BUNDLE) || false;
  const freeTrialLicenses = subscriptions.filter(p => p.isFreeTrial && p.type === planTypes.ZOOM_ONE_BUNDLE).map(p => p?.quantity ?? 0).reduce((p, v) => p + v, 0);
  const hasHealthcareBiz = plansof(boughtBizPlan).category(BASE_PLAN_SUB_TYPES.HC_BIZ).has();
  const totalZOLicenses = bundleLicenses + meetingLicenses;
  const totalLicenseWithoutFreeTrial = totalZOLicenses - freeTrialLicenses;
  const assignedBizPlusLicenseQuantity = getAssignedLicenseQuantity(boughtBizPlusPlan, assignInfo);
  const assignedBizLicenseQuantity = getAssignedLicenseQuantity(boughtBizPlan, assignInfo);
  return {
    pbxBasePlan, // zp base plan
    selectedPbxBasePlan,
    selectedPlans, // Merge getSelectedPlans, boughtPlans plan after deduplication
    selectedBizPlan, // selected biz plans
    boughtBizPlan, // bought biz plans
    selectedBizPlusPlan, // selected biz plus plans
    boughtBizPlusPlan, // bought biz plus plans
    selectedBizBundle: [...selectedBizPlan, ...selectedBizPlusPlan], // selected biz and biz+ plans
    boughtBizBundle: [...boughtBizPlan, ...boughtBizPlusPlan], // bought biz and biz+ plans
    selectedAndBoughtBizPlusPlans,
    // total bundle license
    bundleLicenses,
    // total biz license
    meetingLicenses,
    // biz + biz plus
    totalZOLicenses,
    maxLicensesAddonPlan,
    hasFreeTrial,
    freeTrialLicenses,
    currentSelectedMeetingLicenses,
    hasHealthcareBiz,
    totalLicenseWithoutFreeTrial,
    assignedBizPlusLicenseQuantity,
    assignedBizLicenseQuantity,
  };
};

export const checkBizPlusAddonLicense = () => {
  if (!isBizPlusContext) return { message: '' };
  const { totalZOLicenses: total, maxLicensesAddonPlan: maxLicenseAddon } = store.getters.plansofBizBundle;
  const maxAddonLicense = maxLicenseAddon?.cartInfo?.selected_number;
  let min = maxAddonLicense >= ZOOM_MEETING_PRO_MIN_LICENSE ? maxAddonLicense : ZOOM_MEETING_PRO_MIN_LICENSE;
  if (maxAddonLicense >= ZOOM_MEETING_PRO_MIN_LICENSE && total < maxAddonLicense) {
      return {
        message: translation('billing.meeting_license_decrease_tips_need_decrease_addon', {
          product: translation(maxLicenseAddon.typeNameKey),
        }),
        min,
      };
  } else if (total < min) {
    return {
      message: translation('billing.zobizplus.required_license_warning', [min]),
      min,
    };
  }
  // handle add-on license
  return {
    message: '',
    min,
  };
};

export const checkBizPlusLicensePromise = () => {
  const { message } = checkBizPlusLicense();
  if (message) {
    Vue.prototype.$message.error({ message, dangerouslyUseHTMLString: true });
    return Promise.reject();
  }
  return Promise.resolve();
};

export const checkBizPlusAddonLicensePromise = () => {
  const { message } = checkBizPlusAddonLicense();
  if (message) {
    Vue.prototype.$message.error({ message, dangerouslyUseHTMLString: true });
    return Promise.reject();
  }
  return Promise.resolve();
};

export const getBundleCategoryPlans = (boughtPlans = [], planList = [], targetPlanType = planTypes.ZOOM_ONE_BUNDLE) => {
  const isBulkLicenseAccount = store?.getters?.basicConfig?.isBulkLicenseAccount;
  const oldPlanCode = $query?.oldPlanCode;
  // filter op free trial plan
  boughtPlans = boughtPlans.filter(p => !p.isFreeTrial);
  let bundlePlans = boughtPlans
    .filter((x) => targetPlanType === x.type && BUNDLE_PLAN_CODES_ONLINE_SUPPORTED.includes(x.plan_code));

  if (isBulkLicenseAccount) {
    // add-to-cart
    if (!oldPlanCode && bundlePlans.length) {
      const billingCycle = bundlePlans[0]?.billing_cycle || BILLING_CYCLE.ANNUAL;
      const newPlan = planList.find(p => {
        const hasBought = bundlePlans.find(x => x.plan_code === p.plan_code);
        return !hasBought && p.billing_cycle === billingCycle && targetPlanType === p.type && BUNDLE_PLAN_CODES_ONLINE_SUPPORTED.includes(p.plan_code);
      });
      if (newPlan) {
        return [{
          ...newPlan,
          cartInfo: { selected_number: 10 }
        }].concat(computedPbxPlans(boughtPlans).filter((item) => item.sub_type === PBX_SUB_TYPES.BASE));
      }
    } else if (BUNDLE_PLAN_CODES_ONLINE_SUPPORTED.includes(oldPlanCode)) {
      return boughtPlans
        .filter((p) => p.plan_code === oldPlanCode)
        .concat(
          computedPbxPlans(boughtPlans).filter((item) => item.sub_type === PBX_SUB_TYPES.BASE),
        );
    }
    return [];
  }

  if (!bundlePlans.length) {
    // load us/ca calling plan instead
    // such as japan
    const unsupportBizPlusPlan = boughtPlans.find(p => p.type === targetPlanType);
    const billingCycle = unsupportBizPlusPlan?.billing_cycle || BILLING_CYCLE.ANNUAL;
    const bundlePlan = planList.find(x => billingCycle === x.billing_cycle && targetPlanType === x.type && BUNDLE_PLAN_CODES_ONLINE_SUPPORTED.includes(x.plan_code));
    if (bundlePlan?.plan_code) {
      bundlePlans = [{
        ...bundlePlan,
        cartInfo: {
          selected_number: 10
        }
      }]
    }
  }
  const result = bundlePlans.concat(computedPbxPlans(boughtPlans).filter((item) => item.sub_type === PBX_SUB_TYPES.BASE));
  return result;
};


export const checkBizPlusDownsell = () => {
  let bizPlusExpireTime = null;
  if (isBizPlusContext) {
    const getters = store.getters;
    // for biz and biz plus edit
    const selectedBizPlusPlans = plansof(getters.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
    const boughtBizPlusPlans = plansof(getters.boughtPlans).type(planTypes.ZOOM_ONE_BUNDLE).get();
    boughtBizPlusPlans.forEach(p => {
      const plan = selectedBizPlusPlans.find(item => item.plan_code === p.plan_code);
      if (plan && p.cartInfo.selected_number > plan.cartInfo.selected_number) {
        bizPlusExpireTime = dateTimeFormat(p?.cartInfo?.planExpireTime);
      }
    });
  }
  return {
    bizPlusExpireTime,
  };
};

export const checkBizPlusAssignInfo = () => {
  // for downgrade case
  const { assignInfo, plansofBizBundle } = store.getters;
  return getUnAssignLicenseQuantity({
    plans: plansofBizBundle.selectedBizPlusPlan,
    assignInfo,
  });
};

export const checkBizPlusToBizAssignInfo = () => {
  const { plansofBizBundle } = store.getters;
  const { selectedBizPlan, assignedBizPlusLicenseQuantity } = plansofBizBundle;
  return assignedBizPlusLicenseQuantity - selectedBizPlan[0].cartInfo.selected_number;
};

export const getUnAssignLicenseQuantity = ({ plans = [], quantity, assignInfo }) => {
  let unAssignLicenseQuantity = 0;
  plans.forEach(p => {
    const { assignLicenseQuantity = 0 } = _.get(assignInfo, [p?.plan_code], {});
    const _quantity = quantity || p.cartInfo.selected_number;
    const needAssignQuantity = assignLicenseQuantity - _quantity;
    if (needAssignQuantity > 0) {
      unAssignLicenseQuantity += needAssignQuantity;
    }
  });

  return unAssignLicenseQuantity;
};

export const getAssignedLicenseQuantity = (plans = [], assignInfo = {}) => {
  return plans.reduce((prev, cur) => {
    const assignLicenseQuantity = assignInfo[cur.plan_code]?.assignLicenseQuantity ?? 0;
    if (assignLicenseQuantity > 0) {
      prev += assignLicenseQuantity;
    }
    return prev;
  }, 0);
};

export const getMaxLicensesAddonPlan = (plans) => {
  return plans
    .filter((item) => limitLicenseWithBaseZoneTypes.includes(item.type))
    .reduce((maxPlan, plan) => {
      if (plan.cartInfo.selected_number > get(maxPlan, 'cartInfo.selected_number', 0)) {
        return plan;
      }
      return maxPlan;
    }, {});
};

export const getBundlePbxPlan = ({ boughtPlans, planMapFlat, assignInfo, category, planType = planTypes.ZOOM_ONE_BUNDLE, quantity, defaultCategory = '' }) => {
  const boughtMeetingPlan = getMeetingPlanByCategory(boughtPlans, category);
  const boughtPbxBasePlans = computedPbxBasePlans(boughtPlans);
  const boughtPbxPlans = computedPbxNonBasePlans(boughtPlans);
  let { billing_cycle: billingCycle } = boughtMeetingPlan || {};
  if (boughtPbxBasePlans.length && boughtPbxPlans.length) {
    billingCycle = boughtPbxPlans?.[0]?.billing_cycle;
  }
  const upgradeMeetingPlans = planMapFlat.filter((x) => x.billing_cycle === billingCycle && x.type === planType && BUNDLE_PLAN_CODES_ONLINE_SUPPORTED.includes(x.plan_code));
  const upgradeMeetingPlan = upgradeMeetingPlans.find(p => p.category === defaultCategory || lowerCase(p.category) === defaultCategory) || upgradeMeetingPlans[0];
  const pbxBasePlan = boughtPbxBasePlans?.[0] || computedPbxBasePlans(planMapFlat).find(p => p.category === PBX_CATEGORYS.ZVP);
  const maxLicensesAddonPlan = getMaxLicensesAddonPlan(boughtPlans);
  const assignedBizLicenseQuantity = getAssignedLicenseQuantity([boughtMeetingPlan], assignInfo);
  return {
    [planType]: {
      planCode: upgradeMeetingPlan.plan_code,
      quantity: quantity ? quantity : Math.max(maxLicensesAddonPlan?.cartInfo?.selected_number ?? 0, assignedBizLicenseQuantity, boughtMeetingPlan.cartInfo.selected_number, ZOOM_MEETING_PRO_MIN_LICENSE),
    },
    [planTypes.ZOOM_PHONE]: {
      planCode: pbxBasePlan.plan_code,
      quantity: 1,
    },
  };
};
export const getEdu2EscUpgradePlanQuantity = ({ boughtPlans, assignInfo, category }) => {
  const boughtMeetingPlan = getMeetingPlanByCategory(boughtPlans, category);
  const maxLicensesAddonPlan = getMaxLicensesAddonPlan(boughtPlans);
  const assignedBizLicenseQuantity = getAssignedLicenseQuantity([boughtMeetingPlan], assignInfo);
  return  Math.max(maxLicensesAddonPlan?.cartInfo?.selected_number ?? 0, assignedBizLicenseQuantity, boughtMeetingPlan.cartInfo.selected_number, ZOOM_MEETING_PRO_MIN_LICENSE)
};

export const getProp2BundleUpgradePlan = ({ boughtPlans, planMapFlat }) => {
  const proplus = boughtPlans.find(p => p.type === planTypes.ZONE_PRO_PLUS);
  const boughtPbxBasePlans = computedPbxBasePlans(boughtPlans);
  const boughtPbxPlans = computedPbxNonBasePlans(boughtPlans);
  let { billing_cycle: billingCycle } = proplus || {};
  if (boughtPbxBasePlans.length && boughtPbxPlans.length) {
    billingCycle = boughtPbxPlans?.[0]?.billing_cycle;
  }
  const upgradeMeetingPlan = planMapFlat.find((x) => x.billing_cycle === billingCycle && x.type === planTypes.ZOOM_ONE_BUNDLE && BUNDLE_PLAN_CODES_ONLINE_SUPPORTED.includes(x.plan_code));
  const pbxBasePlan = boughtPbxBasePlans?.[0] || computedPbxBasePlans(planMapFlat).find(p => p.category === PBX_CATEGORYS.ZVP);
  return {
    [planTypes.ZOOM_ONE_BUNDLE]: {
      planCode: upgradeMeetingPlan.plan_code,
      quantity: Math.max(proplus.cartInfo.selected_number, ZOOM_MEETING_PRO_MIN_LICENSE),
    },
    [planTypes.ZOOM_PHONE]: {
      planCode: pbxBasePlan.plan_code,
      quantity: 1,
    },
  };
};

export const getZs2ZeUpgradePlan = ({ boughtPlans, planMapFlat }) => {
  let { plan: planSubType, oldPlanCode, period } = pageConfig;
  //ppa
  if (ZOOM_EVENTS_SUB_TYPES.EVENT_SAAS_CORE_CONSUMPTION === planSubType) {
    const { attendees } = parseConsumptionCode(oldPlanCode);
    let zePlan = planMapFlat.find(plan => plan.type === planTypes.EVENT_SAAS && plan.billing_cycle === BILLING_CYCLE.PAY_PER_ATTENDEE && plan.capacity === parseInt(attendees));
    if(!zePlan) {
      zePlan = planMapFlat.find(plan => plan.type === planTypes.EVENT_SAAS && plan.billing_cycle === BILLING_CYCLE.PAY_PER_ATTENDEE);
    }

    return {
      [planTypes.EVENT_SAAS]: {
        planCode: zePlan?.plan_code,
        quantity: 1,
      },
    };
  } else {
    //saas
    const oldZsPlan = boughtPlans.find(bp => bp.plan_code === oldPlanCode);
    if(!oldZsPlan) return {};

    period = period ?? oldZsPlan.billing_cycle;
    const { plans: basePlans } = getBasePlanInfo(boughtPlans);
    if (BillingCycleEnum[period] > BillingCycleEnum[basePlans[0]?.billing_cycle]) {
      period = basePlans[0].billing_cycle;
    }

    const zePlans = planMapFlat.filter(plan => plan.type === planTypes.EVENT_SAAS);

    let upgradeZePlan = zePlans.find(
      plan => period === plan.billing_cycle
        && oldZsPlan.capacity === plan.capacity
        && !boughtPlans.some(bp => bp.plan_code === plan.plan_code));

    if (!upgradeZePlan) {
      upgradeZePlan = zePlans.find(plan => !boughtPlans.some(bp => bp.plan_code === plan.plan_code));
    }

    return {
      [planTypes.EVENT_SAAS]: {
        planCode: upgradeZePlan?.plan_code,
        quantity: oldZsPlan?.cartInfo?.selected_number || 1,
      },
    };
  }
};

export const getCategoryNameForPbx = (category) => {
  return category ? translation(`billing.pbx.plan_name.${category}`) : '';
};

export const zoneBundleUtils = {
  PRO_PLUS_CATEGORY: 'zoneproplus_zonepropzvu',
  BIZ_PLUS_CATEGORY: 'zonebundle_zonebizpzvu',
  isProPlusByCategory(category = '') {
    return _.includes(category.toUpperCase(), ZONE_PRO_PLUS_PARENT_CATEGORY);
  },
  isBizPlusByCategory(category = '') {
    return _.includes(category.toUpperCase(), ZONE_BIZ_PLUS_PARENT_CATEGORY);
  },
  isEduPlusByCategory(category = '') {
    return _.includes(category.toUpperCase(), 'ZONEDUSCHCMPP');
  },
  isBizPlusByPlanType(planType) {
    return planType === planTypes.ZOOM_ONE_BUNDLE;
  },
  isProPlusByPlanType(planType) {
    return planType === planTypes.ZONE_PRO_PLUS;
  },
  isEduPlusByPlanType(planType) {
    return planType === planTypes.ZONE_EDU_PLUS;
  },
  getBundlePlanDefaultLicense(planType) {
    return this.isBizPlusByPlanType(planType) ? ZOOM_MEETING_PRO_MIN_LICENSE : (this.isEduPlusByPlanType(planType) ? ZOOM_MEETING_EDU_PLUS_MIN_LICENSE : 1);
  },
  isZoneBundleByPlanType(planType) {
    return this.isBizPlusByPlanType(planType) || this.isProPlusByPlanType(planType) || this.isEduPlusByPlanType(planType)
  }
};

export const isInUnlockBizPlusCountRetentionFlow = () => {
  return $context.flow === flowTypes.edit &&
  unlockBizAndBizPlusCountByRetention &&
  plansof(store.getters.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).has();
}

// Postone to Dec release
// export const reactivateBizPlusIfCancelled = async () => {
//   const { boughtPlans } = store.getters
//   const bizPlusPlan = plansof(boughtPlans).type(planTypes.ZOOM_ONE_BUNDLE).get(0)
//   if (bizPlusPlan && bizPlusPlan.cancelled === 1) {
//     await reactivateSubPlan(bizPlusPlan)
//   }
// }
